// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import type { FileFootprint } from '../footprintSelectors';

// Matches the existing combined LAS bounding box colour (MapLasBBox), so red
// keeps meaning "point cloud" on this map.
export const LAS_COLOR = '#ff0000';
// The brand orange (themes.ts lightOrangeColor): pano coverage boxes (D76).
export const PANO_COLOR = '#FAA738';

// The tif colour comes from the theme (secondary cyan), so callers pass it in.
export const kindLineColor = (kind: FileSelectionKind, tifColor: string): string => {
  if (kind === 'las') return LAS_COLOR;
  if (kind === 'pano') return PANO_COLOR;
  return tifColor;
};

// Dash patterns per kind, shared by the map line layers and the switch card's
// legend swatches so they never drift apart.
export const KIND_DASHES: Partial<Record<FileSelectionKind, [number, number]>> = {
  las: [4, 2],
  pano: [2, 2],
};

export interface FootprintFeatureProps {
  id: string;
  kind: FileSelectionKind;
  name: string;
  hovered: boolean;
  selected: boolean;
}

export type FootprintFeature = GeoJSON.Feature<GeoJSON.Polygon, FootprintFeatureProps>;

const footprintRing = (b: [number, number, number, number]): GeoJSON.Position[] => [
  [b[0], b[1]], [b[2], b[1]], [b[2], b[3]], [b[0], b[3]], [b[0], b[1]],
];

export const nwCorner = (b: [number, number, number, number]): [number, number] => [b[0], b[3]];

const footprintProperties = (
  fp: FileFootprint,
  hovered: IHoveredFiles | null,
  selected: Record<FileSelectionKind, string[]>,
): FootprintFeatureProps => ({
  id: fp.id,
  kind: fp.kind,
  name: fp.name,
  hovered: !!hovered && hovered.files.some((f) => f.kind === fp.kind && f.id === fp.id),
  selected: selected[fp.kind].includes(fp.id),
});

// Hover/selected ride on feature PROPERTIES (not setFeatureState): the
// streets/satellite toggle swaps the whole Mapbox style, which silently drops
// feature-state, while a property-carrying source survives the swap.
export const buildFootprintCollection = (
  footprints: FileFootprint[],
  hovered: IHoveredFiles | null,
  selected: Record<FileSelectionKind, string[]>,
): GeoJSON.FeatureCollection<GeoJSON.Polygon, FootprintFeatureProps> => ({
  type: 'FeatureCollection',
  features: footprints.map((fp) => ({
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [footprintRing(fp.bounds)] },
    properties: footprintProperties(fp, hovered, selected),
  })),
});

// Outlines are LINESTRINGS, not polygons: clipping a polygon closes its ring
// along the tile boundary, painting phantom edges wherever a boundary crosses
// a box; a clipped LineString just ends (D70). With the unbuffered outline
// source each edge is drawn by exactly one tile, so dashes cannot double up.
export const buildFootprintOutlineCollection = (
  footprints: FileFootprint[],
  hovered: IHoveredFiles | null,
  selected: Record<FileSelectionKind, string[]>,
): GeoJSON.FeatureCollection<GeoJSON.LineString, FootprintFeatureProps> => ({
  type: 'FeatureCollection',
  features: footprints.map((fp) => ({
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: footprintRing(fp.bounds) },
    properties: footprintProperties(fp, hovered, selected),
  })),
});

// Focus feedback for tifs dims the OTHER rasters (MapRasterLayer's
// raster-opacity, driven by getFadedRasterStems) instead of tinting imagery,
// so the tif fill is mostly hit-area. LAS/pano boxes keep tint feedback — no
// cloud imagery renders on this map (D45/D46).
export const footprintFillPaint = (
  tifColor: string,
  lasColor: string,
  panoColor: string,
): { 'fill-color': mapboxgl.Expression; 'fill-opacity': mapboxgl.Expression } => ({
  'fill-color': [
    'case',
    ['==', ['get', 'kind'], 'las'], lasColor,
    ['==', ['get', 'kind'], 'pano'], panoColor,
    tifColor,
  ],
  // Hovered/selected tifs get a light tint (D79): when a tile's imagery
  // covers only a sliver of its bbox, the raster fade alone leaves the box
  // looking empty, so you cannot tell WHICH box you are on. Kept below the
  // las/pano 0.2 so imagery stays readable; 0.08 proved imperceptible live,
  // 0.3 clearly visible - these sit in between.
  'fill-opacity': [
    'case',
    ['==', ['get', 'kind'], 'tif'],
    ['case', ['get', 'selected'], 0.15, ['get', 'hovered'], 0.1, 0],
    ['case', ['get', 'selected'], 0.2, ['get', 'hovered'], 0.1, 0],
  ],
});

export const KIND_ORDER: Record<FileSelectionKind, number> = { tif: 0, las: 1, pano: 2, poleImage: 3 };

// Multi-file card ordering for stacked hits: tifs, then las, then panos, then by name.
export const orderFootprintHits = <T extends { kind: FileSelectionKind; name: string }>(hits: T[]): T[] =>
  [...hits].sort((a, b) => {
    if (a.kind !== b.kind) return KIND_ORDER[a.kind] - KIND_ORDER[b.kind];
    return a.name.localeCompare(b.name);
  });
